<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { RouterLink, useRoute } from 'vue-router';

const props = defineProps({
    // Lista de { label, rota, badge?, badgeTitulo?, badgeAlerta? }, onde `rota` é o nome da rota de cada aba,
    // `badge` é um contador exibido ao lado do label quando maior que zero e `badgeAlerta` o deixa vermelho.
    // Com v-model, cada aba usa `valor` no lugar de `rota` e o clique só troca o valor, sem navegar
    abas: {
        type: Array,
        required: true,
    },
    // Leva a query atual (filtros, paginação) para a aba de destino
    manterQuery: {
        type: Boolean,
        default: false,
    },
    modelValue: {
        type: [String, Number],
        default: undefined,
    },
});

const emit = defineEmits(['update:modelValue']);

const route = useRoute();

const porEstado = computed(() => props.modelValue !== undefined);
const chaveAtiva = computed(() => String(porEstado.value ? props.modelValue : route.name));

const chave = (aba) => String(porEstado.value ? aba.valor : aba.rota);
const estaAtiva = (aba) => chave(aba) === chaveAtiva.value;

// Por rota a aba é um RouterLink; por estado é um wrapper simples e o próprio Button recebe o foco
const atributosAba = (aba) => (porEstado.value
    ? { onClick: () => emit('update:modelValue', aba.valor) }
    : { to: { name: aba.rota, query: props.manterQuery ? route.query : undefined } });

const PT = { root: { class: 'p-2' } };

const rolagemRef = ref(null);
const containerRef = ref(null);
const pastilhaRef = ref(null);
const hoverRef = ref(null);

let observador = null;

const temBrowser = typeof window !== 'undefined' && typeof window.matchMedia === 'function';

const semAnimacao = () =>
    temBrowser ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : true;

const medir = (el) => ({
    x: el.offsetLeft,
    y: el.offsetTop,
    largura: el.offsetWidth,
    altura: el.offsetHeight,
});

const classesBadge = (aba) => {
    if (aba.badgeAlerta) return 'bg-red-100 text-red-700';

    return estaAtiva(aba) ? 'bg-white text-600' : 'surface-100 border-1 surface-border text-400';
};

const abaAtiva = () => containerRef.value?.querySelector(`[data-aba="${chaveAtiva.value}"]`);

const transformar = (x, y, escalaX = 1, escalaY = 1) =>
    `translate(${x}px, ${y}px) scale(${escalaX}, ${escalaY})`;

// Lê a posição visível no momento, inclusive no meio de uma animação, para a próxima partir dali
const posicaoAtual = (el) => {
    const matriz = new DOMMatrixReadOnly(getComputedStyle(el).transform);
    return { x: matriz.m41, largura: matriz.a * el.offsetWidth };
};

/*
 * Só transform e opacity são animados: rodam no compositor e não travam quando a troca de rota
 * ocupa a thread principal. A largura final é aplicada de uma vez e o scaleX faz o "esticar".
 */
const moverPastilha = (el, destino, { animar, duracao, achatar }) => {
    const origem = posicaoAtual(el);

    el.getAnimations().forEach((animacao) => animacao.cancel());
    el.style.width = `${destino.largura}px`;
    el.style.height = `${destino.altura}px`;
    el.style.transform = transformar(destino.x, destino.y);

    if (!animar || semAnimacao() || !origem.largura) return;

    // No meio do caminho a pastilha cobre a origem e o destino ao mesmo tempo, como um líquido
    const inicio = Math.min(origem.x, destino.x);
    const fim = Math.max(origem.x + origem.largura, destino.x + destino.largura);

    el.animate([
        { transform: transformar(origem.x, destino.y, origem.largura / destino.largura), easing: 'cubic-bezier(0.4, 0, 0.6, 1)' },
        { transform: transformar(inicio, destino.y, (fim - inicio) / destino.largura, achatar ? 0.86 : 1), offset: 0.4, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' },
        { transform: transformar(destino.x, destino.y) },
    ], { duration: duracao });
};

// Ao desmontar, o focusout do elemento removido chega com os refs já nulos
const mostrar = (el, visivel) => {
    if (!el) return;

    el.style.opacity = visivel ? '1' : '0';
};

const atualizarPastilha = (animar) => {
    const alvo = abaAtiva();

    if (!pastilhaRef.value) return;

    if (!alvo) {
        mostrar(pastilhaRef.value, false);
        return;
    }

    const estavaVisivel = pastilhaRef.value.style.opacity === '1';

    moverPastilha(pastilhaRef.value, medir(alvo), { animar: animar && estavaVisivel, duracao: 380, achatar: true });
    mostrar(pastilhaRef.value, true);
};

// No mobile a aba ativa pode estar fora da área visível; rola só na horizontal para não mexer na página
const mostrarAbaAtiva = (animar) => {
    const alvo = abaAtiva();
    const rolagem = rolagemRef.value;

    if (!alvo || !rolagem) return;

    const inicio = alvo.offsetLeft;
    const fim = inicio + alvo.offsetWidth;

    if (inicio >= rolagem.scrollLeft && fim <= rolagem.scrollLeft + rolagem.clientWidth) return;

    rolagem.scrollTo({
        left: inicio - (rolagem.clientWidth - alvo.offsetWidth) / 2,
        behavior: animar && !semAnimacao() ? 'smooth' : 'auto',
    });
};

const esconderHover = () => mostrar(hoverRef.value, false);

// O fundo do hover estica de uma aba para a outra em vez de piscar em cada uma
const aoEntrarNaAba = (evento) => {
    // Na aba ativa o hover adiantaria o fundo antes da pastilha terminar de chegar
    if (evento.currentTarget.dataset.aba === chaveAtiva.value) {
        esconderHover();
        return;
    }

    if (!hoverRef.value) return;

    const estavaVisivel = hoverRef.value.style.opacity === '1';

    moverPastilha(hoverRef.value, medir(evento.currentTarget), { animar: estavaVisivel, duracao: 260, achatar: false });
    mostrar(hoverRef.value, true);
};

const aoSairDasAbas = (evento) => {
    // Foco indo de uma aba para outra pelo teclado não deve esconder o hover
    if (containerRef.value?.contains(evento.relatedTarget)) return;

    esconderHover();
};

// Sem nextTick: a animação começa antes da tela nova ser montada pela troca de rota
watch(chaveAtiva, () => {
    esconderHover();
    atualizarPastilha(true);
    mostrarAbaAtiva(true);
});

// Observa cada aba, e não só o container, para pegar mudanças de largura de uma aba (ex.: badge)
const observarAbas = () => {
    observador.disconnect();
    observador.observe(containerRef.value);
    containerRef.value.querySelectorAll('[data-aba]').forEach((aba) => observador.observe(aba));
};

// Abas que aparecem depois (permissões, dados carregados) deslocam as demais
watch(() => props.abas, async () => {
    await nextTick();
    observarAbas();
    atualizarPastilha(false);
}, { deep: true });

onMounted(() => {
    atualizarPastilha(false);
    mostrarAbaAtiva(false);

    // Reposiciona sem animar quando a largura das abas muda (carregamento da fonte, resize)
    observador = new ResizeObserver(() => atualizarPastilha(false));
    observarAbas();
});

onBeforeUnmount(() => observador?.disconnect());
</script>

<template>
    <div ref="rolagemRef" class="aba-rolagem overflow-x-auto max-w-full mb-4">
        <div ref="containerRef" class="relative inline-flex flex-row gap-2 align-items-center" @mouseleave="aoSairDasAbas"
            @focusout="aoSairDasAbas">
            <span ref="hoverRef" class="aba-hover aba-fundo absolute top-0 left-0 border-round pointer-events-none"
                aria-hidden="true"></span>
            <span ref="pastilhaRef"
                class="aba-pastilha aba-fundo absolute top-0 left-0 surface-200 border-round pointer-events-none"
                aria-hidden="true"></span>

            <component :is="porEstado ? 'div' : RouterLink" v-for="aba in abas" :key="chave(aba)" v-bind="atributosAba(aba)" :data-aba="chave(aba)"
                class="aba-link relative inline-flex flex-shrink-0 no-underline" @mouseenter="aoEntrarNaAba" @focusin="aoEntrarNaAba">
                <Button severity="secondary" size="small" :pt="PT"
                    class="aba-botao border-none outline-none focus:shadow-none white-space-nowrap"
                    :class="estaAtiva(aba) ? 'text-900' : 'aba-botao--inativa text-400'">
                    <div class="flex flex-row gap-1 align-items-center">
                        <p class="font-medium text-xs m-0">{{ aba.label }}</p>
                        <Badge v-if="aba.badge > 0" :value="String(aba.badge)" :title="aba.badgeTitulo"
                            class="aba-badge font-medium flex align-items-center justify-content-center"
                            :class="classesBadge(aba)" />
                    </div>
                </Button>
            </component>
        </div>
    </div>

</template>

<style scoped>
/* No mobile as abas rolam na horizontal sem mostrar a barra de rolagem */
.aba-rolagem {
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
}

.aba-rolagem::-webkit-scrollbar {
    display: none;
}

.aba-fundo {
    opacity: 0;
    transform-origin: left center;
    will-change: transform, opacity;
    transition: opacity 0.18s ease;
}

.aba-hover {
    z-index: 0;
    background: rgba(17, 17, 17, 0.034);
}

.aba-pastilha {
    z-index: 1;
}

/* Afunda rápido ao pressionar e volta com um leve overshoot ao soltar */
.aba-link {
    z-index: 2;
    transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.aba-link:active {
    transform: scale(0.94);
    transition: transform 0.1s ease-out;
}

/* O fundo vem das pastilhas; o botão fica transparente para elas aparecerem por trás */
.p-button.aba-botao,
.p-button.aba-botao:enabled:hover,
.p-button.aba-botao:enabled:active {
    background: transparent;
    transition: color 0.25s ease;
}

.p-button.aba-botao--inativa:enabled:hover {
    color: var(--surface-600, #707074);
}

.aba-badge {
    font-size: 10px;
    padding: 0 4px;
    transition: background-color 0.25s ease, color 0.25s ease;
}

@media (prefers-reduced-motion: reduce) {

    .aba-fundo,
    .aba-link,
    .aba-link:active,
    .p-button.aba-botao {
        transition: none;
    }

    .aba-link:active {
        transform: none;
    }
}
</style>
